import { useSyncExternalStore } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import englishMessages from './en.json';

export type Language = 'ja' | 'en';
export const LANGUAGE_STORAGE_KEY = '@app_language';
const messages: Record<string, string> = englishMessages;
const listeners = new Set<() => void>();
let language: Language = 'ja';

export function translate(message: string, locale: Language, values: readonly unknown[] = []): string {
  let result = locale === 'en' ? messages[message] ?? message : message;
  if (locale === 'en' && !messages[message]) {
    result = result.replace(/^(\d{4})年度$/, '$1').replace(/^(\d+)限$/, 'Period $1');
    result = result.replace(/^([月火水木金土日])(?:曜)?(\d+)限$/, (_, day, period) => `${messages[day]}, Period ${period}`);
  }
  return result.replace(/\{(\d+)\}/g, (placeholder, index) =>
    Number(index) < values.length ? String(values[Number(index)] ?? '') : placeholder);
}

// Only call this for UI labels, never for identifiers or user-entered/course data.
export const t = (message: string, values?: readonly unknown[]) => translate(message, language, values);
export const getLanguage = () => language;

function applyLanguage(next: Language) {
  language = next;
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  listeners.forEach(listener => listener());
}

export function initializeLanguage(): Promise<void> {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      applyLanguage(stored === 'en' ? 'en' : 'ja');
    } catch (error) {
      console.warn('Could not restore display language', error);
    }
    return Promise.resolve();
  }
  return AsyncStorage.getItem(LANGUAGE_STORAGE_KEY).then(stored => {
    applyLanguage(stored === 'en' ? 'en' : 'ja');
  }).catch(error => console.warn('Could not restore display language', error));
}

export async function setLanguage(next: Language): Promise<void> {
  // Persist first so a failed write never appears to have succeeded.
  if (typeof window !== 'undefined') window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  else await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  applyLanguage(next);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useLanguage() {
  const current = useSyncExternalStore(subscribe, getLanguage, () => 'ja' as Language);
  return { language: current, setLanguage, t };
}
